import { z } from 'zod';

const pronounInfoSchema = z
  .object({
    patientId: z.number().nullish(),
    patientGuid: z.string().nullish(),
    pronounCode: z.string().nullish(),
    pronounDetail: z.string().nullish(),
  })
  .passthrough();

const patientSummarySchema = z
  .object({
    patientId: z.number().nullish(),
    practiceId: z.number().nullish(),
    genderId: z.number().nullish(),
    genderIdentityId: z.number().nullish(),
    sexualOrientationId: z.number().nullish(),
    pronounInfo: pronounInfoSchema.nullish(),
    dateOfBirth: z.number().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    alias: z.string().nullish(),
    preferredPhoneType: z.string().nullish(),
    preferredEmailType: z.string().nullish(),
    homePhone: z.string().nullish(),
    email: z.string().nullish(),
    middleName: z.string().nullish(),
    workPhone: z.string().nullish(),
    mobilePhone: z.string().nullish(),
    otherPhone: z.string().nullish(),
    workEmail: z.string().nullish(),
    otherEmail: z.string().nullish(),
    guid: z.string().nullish(),
  })
  .passthrough();

export const appointmentSchema = z
  .object({
    appointmentGuid: z.string().nullish(),
    customerId: z.number().nullish(),
    practiceGuid: z.string().nullish(),
    serviceLocationGuid: z.string().nullish(),
    patientSummaries: z.array(patientSummarySchema).nullish(),
    patientGuid: z.string().nullish(),
    primaryAppointmentReasonGuid: z.string().nullish(),
    appointmentReasonGuids: z.array(z.string()).nullish(),
    providerGuids: z.array(z.string()).nullish(),
    resourceGuids: z.array(z.string()).nullish(),
    startTime: z.number().nullish(),
    endTime: z.number().nullish(),
    timeZoneId: z.string().nullish(),
    appointmentStatus: z.string().nullish(),
    appointmentType: z.string().nullish(),
    appointmentMode: z.string().nullish(),
    notes: z.string().nullish(),
    createdAt: z.number().nullish(),
    createdBy: z.string().nullish(),
    updatedAt: z.number().nullish(),
    updatedBy: z.string().nullish(),
    recurrenceRule: z.unknown().nullish(),
    occurrenceId: z.unknown().nullish(),
    ticketNumber: z.string().nullish(),
    exceptionCalendarDates: z.string().nullish(),
    appointmentName: z.string().nullish(),
    maxAttendees: z.number().nullish(),
    attendeesCount: z.number().nullish(),
    groupAttendeeGuids: z.array(z.string()).nullish(),
    wasCreatedOnline: z.boolean().nullish(),
    origin: z.string().nullish(),
    insurancePolicyAuthorizationId: z.number().nullish(),
    patientCaseId: z.number().nullish(),
    uniqueIdForUrl: z.string().nullish(),
    allDay: z.boolean().nullish(),
    isRecurring: z.boolean().nullish(),
    isGroupAppointment: z.boolean().nullish(),
    isForRecare: z.boolean().nullish(),
  })
  .passthrough();

export const patientSchema = z
  .object({
    patientId: z.number(),
    practiceId: z.number().nullish(),
    guid: z.string().nullish(),
    firstName: z.string().nullish(),
    middleName: z.string().nullish(),
    lastName: z.string().nullish(),
    mrn: z.unknown().nullish(),
    dob: z.string().nullish(),
    gender: z.string().nullish(),
    email: z.string().nullish(),
    ageInYears: z.number().nullish(),
    monthsSinceBirthday: z.number().nullish(),
    primaryInsurance: z.string().nullish(),
    secondaryInsurance: z.unknown().nullish(),
    alertMessage: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    phoneType: z.string().nullish(),
    address1: z.string().nullish(),
    address2: z.string().nullish(),
    city: z.string().nullish(),
    zipCode: z.string().nullish(),
    state: z.string().nullish(),
  })
  .passthrough();
type Role = {
  id: number;
  pmRoleId: number;
  name: string;
  description: string | null;
  privileges: any | null;
  pmProvider: boolean;
  default: boolean;
};

export type UserResponse = {
  userId: number;
  userGuid: string;
  userName: string;
  firstName: string;
  lastName: string;
  gender: string | null;
  email: string;
  password: string | null;
  hidden: boolean;
  createdAt: number;
  updatedAt: number | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  fax: string | null;
  npi: string | null;
  dea: string | null;
  stateLicense: string | null;
  roles: Role[];
  pmUserId: number;
  isCustomerAdmin: boolean;
  stub: boolean;
};

export type Provider = {
  providerId: number;
  providerGuid: string;
  practiceId: number;
  userId: number;
  enterpriseSiteUserId: number;
  npi: string;
  active: boolean;
  createdAt: number | null;
  updatedAt: number | null;
  createdBy: number;
  updatedBy: number;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  userName: string | null;
  gender: string | null;
  userGuid: string;
  taxonomyCode: string;
  degree: string;
  ssn: string;
  kareoSpecialtyId: number;
  billingCredentialTypeId: number;
  providerTypeId: number;
  workPhone: string;
  isReferringProvider: boolean;
  isUserSync: boolean;
  telehealthUrl: string;
};

export type Appointment = z.infer<typeof appointmentSchema>;
export type AppointmentsResponse = Appointment[];
export type Patient = z.infer<typeof patientSchema>;
